import { all, call, put, select } from 'redux-saga/effects';
import { authSelectors } from '../../auth/auth.selectors';
import { userSelectors } from '../user/user.selectors';
import { IUser } from '../user/user.types';
import { settingsSelectors } from '../settings/settings.selectors';
import { getWrikeFoldersApi, syncWrikeFoldersApi, removeWrikeIntegrationApi } from '../../api/request-config/wrike.api';
import { apiRequest } from '../../api/api.saga-services';
import { storeSettings } from '../settings/settings.actions';
import { storeWrikeFolder, clearWrikeFolder } from './wrike-folder.actions';
import * as types from '../settings/settings.types';
import * as api from '../../api/request-config/wrike.api';
import { ISyncWrikeFoldersPayload, IWrikeFoldersParams } from '../../page/page-storage/wrike/wrike.types';
import { wrikeFolderSelectors } from './wrike-folder.selectors';
import { storeWrikeFolderMeeting, clearWrikeFolderMeeting } from '../wrike-folder-meeting/wrike-folder-meeting.actions';
import { wrikeFolderMeetingSelectors } from '../wrike-folder-meeting/wrike-folder-meeting.selectors';

export function* clearWrikeFolderSaga(wrikeFolderId: string) {
  yield put(clearWrikeFolder(wrikeFolderId));
}

export function* clearAllWrikeFolders() {
  const folderIds = yield select(wrikeFolderSelectors.selectResourceIds);
  yield put(clearWrikeFolder(folderIds));
}

export function* integrateWrike(code: string, state: string) {
  const loggedUserId = yield select(authSelectors.selectLoggedUser);
  const loggedUser = (yield select(userSelectors.selectResourceById, loggedUserId ? loggedUserId : '')) as IUser;
  const tenantId = loggedUser && loggedUser.tenantId ? loggedUser.tenantId : '';
  const settings = (yield select(settingsSelectors.selectResourceById, tenantId)) as types.ISettings;

  const response = yield call(apiRequest, api.integrateWrikeApi(code, state));
  if (settings) {
    const updatedSettings: types.ISettings = {
      ...settings,
      wrikeWorkspaceName: response?.accountName || '',
    };
    yield put(storeSettings(updatedSettings));
  }
}

export function* fetchWrikeFolders(params?: IWrikeFoldersParams) {
  const folders = yield call(apiRequest, getWrikeFoldersApi(params));
  if (Array.isArray(folders) && folders.length > 0) {
    yield put(storeWrikeFolder(folders));
  }
}

export function* syncWrikeFolders(payload: ISyncWrikeFoldersPayload) {
  const response = yield call(apiRequest, syncWrikeFoldersApi(payload));
  const existingIds = yield select(wrikeFolderMeetingSelectors.selectResourceIds);
  const returnedMeetings = Array.isArray(response) ? response : [];
  const returnedIds = returnedMeetings.map((m) => m.id);

  if (Array.isArray(existingIds) && existingIds.length > 0) {
    const idsToClear = existingIds.filter((id: string) => !returnedIds.includes(id));
    if (idsToClear.length > 0) {
      yield put(clearWrikeFolderMeeting(idsToClear));
    }
  }

  if (returnedMeetings.length > 0) {
    yield all(returnedMeetings.map((meeting) => put(storeWrikeFolderMeeting(meeting))));
  }

  return response;
}

export function* removeWrikeIntegration() {
  const loggedUserId = yield select(authSelectors.selectLoggedUser);
  const loggedUser = (yield select(userSelectors.selectResourceById, loggedUserId ? loggedUserId : '')) as IUser;
  const tenantId = loggedUser && loggedUser.tenantId ? loggedUser.tenantId : '';
  const settings = (yield select(settingsSelectors.selectResourceById, tenantId)) as types.ISettings;

  yield call(apiRequest, removeWrikeIntegrationApi());

  // Clear wrike folders and folder-meetings from state
  yield call(clearAllWrikeFolders);
  const folderMeetingIds = yield select(wrikeFolderMeetingSelectors.selectResourceIds);
  if (Array.isArray(folderMeetingIds) && folderMeetingIds.length > 0) {
    yield put(clearWrikeFolderMeeting(folderMeetingIds));
  }

  // Update settings to remove wrike workspace name
  if (settings) {
    const updatedSettings: types.ISettings = {
      ...settings,
      wrikeWorkspaceName: '',
    };
    yield put(storeSettings(updatedSettings));
  }
}
